import React, { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import { venues } from "../mock/venues";
import { guideNav } from "../mock/guide";

const guideItems = guideNav.filter((n) => !venues.some((v) => v.slug === n.id));
const historyIdx = guideItems.findIndex((g) => g.id === "history");
const leadItems = guideItems.slice(0, historyIdx);
const trailItems = guideItems.slice(historyIdx);

const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

/** Collect every anchor id on the page, in order, for the active-section highlight */
const allIds = [
  ...leadItems.map((g) => g.id),
  ...venues.flatMap((v) => [v.slug, ...v.nav.map((n) => `${v.slug}-${n.id}`)]),
  ...trailItems.map((g) => g.id),
];

const SideNav = ({ onNavigate }) => {
  const [active, setActive] = useState("");

  useEffect(() => {
    const onScroll = () => {
      let current = "";
      allIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 170) current = id;
      });
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    scrollToId(id);
    if (onNavigate) onNavigate();
  };

  const linkClass = (id, indent = false, theme = null) =>
    `block w-full text-left rounded px-2.5 py-1.5 transition-colors ${
      indent ? "pl-5 text-[0.78rem]" : "text-[0.84rem]"
    } ${
      active === id
        ? theme
          ? `${theme.bg} ${theme.accent} font-semibold`
          : "bg-primary/10 text-primary font-semibold"
        : "text-muted-foreground hover:bg-muted hover:text-foreground"
    }`;

  return (
    <nav aria-label="Contents" className="text-[0.86rem]">
      <p className="mb-3 border-b border-border pb-2 font-serif text-[0.95rem] text-foreground">
        Contents
      </p>

      <ul className="space-y-0.5">
        {leadItems.map((g) => (
          <li key={g.id}>
            <button
              data-testid={`sidenav-${g.id}`}
              onClick={() => go(g.id)}
              className={linkClass(g.id)}
            >
              {g.label}
            </button>
          </li>
        ))}
      </ul>

      <p className="mb-2 mt-6 border-b border-border pb-2 font-serif text-[0.95rem] text-foreground">
        Venues
      </p>

      <ul className="space-y-2">
        {venues.map((v) => (
          <li key={v.slug}>
            <button
              data-testid={`sidenav-${v.slug}`}
              onClick={() => go(v.slug)}
              className={`flex w-full items-center gap-1 rounded px-2.5 py-1.5 text-left text-[0.84rem] font-medium transition-colors ${
                active && active.startsWith(v.slug)
                  ? `${v.theme.bg} ${v.theme.accent}`
                  : "text-foreground hover:bg-muted"
              }`}
            >
              <ChevronRight
                className={`h-3.5 w-3.5 flex-shrink-0 ${v.theme.accent}`}
              />
              {v.name}
            </button>
            <ul className="mt-0.5 space-y-0.5">
              {v.nav.map((n) => (
                <li key={n.id}>
                  <button
                    data-testid={`sidenav-${v.slug}-${n.id}`}
                    onClick={() => go(`${v.slug}-${n.id}`)}
                    className={linkClass(`${v.slug}-${n.id}`, true, v.theme)}
                  >
                    {n.label}
                  </button>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>

      <p className="mb-2 mt-6 border-b border-border pb-2 font-serif text-[0.95rem] text-foreground">
        More
      </p>

      <ul className="space-y-0.5">
        {trailItems.map((g) => (
          <li key={g.id}>
            <button
              data-testid={`sidenav-${g.id}`}
              onClick={() => go(g.id)}
              className={linkClass(g.id)}
            >
              {g.label}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default SideNav;
